import React from "react";
import vi from "./trans-vi";
import { useTranslation } from "react-i18next";
import { Button, useTheme } from "react-native-paper";
import { StyleSheet } from "react-native";
import { Block } from "app/shared/components/blocks/block";
import { RegisterOptions, useForm } from "react-hook-form";
import { spacing } from "app/shared/styles/spacing";
import { registerTranslation } from "react-native-paper-dates";
import { useDesktopMediaQuery } from "app/shared/util/media-query";
import { InputSelect, InputText } from "app/shared/components/form/input";
import { CARDITEM } from "app/shared/services/services-model.type";
import { RouteProp, useRoute } from "@react-navigation/core";
import StackParamsList from "app/navigation/nav-stack-params";
import ScreenNameType from "app/shared/constant/nav-screen.type";
import { useDispatch, useSelector } from "react-redux";
import { getCardListSelector } from "../../reducer/selectors";
import CardSourceModal from "app/shared/components/modal/card-source/card-source.modal";
import { fetchCardListRequest } from "../../reducer/actions";
import { QueueSpacing } from "app/shared/components/spacing";
import { formatNumberThousand } from "app/shared/util/format-currency";
import { ArrayCreditShow, getCardValidStatus } from "../../card-utils/card-utils";
import { Constant } from "app/shared/constant/constant";

export type FormFilterCardStatement = {
  cardID: string;
  cardNum: string;
  embossName: string;
  availBalance: string;
};

interface FilterCardStatementTableProps {
  onChangeSearch: (values: FormFilterCardStatement) => void;
  card?: CARDITEM;
  showModal: Function;
  closeModal: Function;
  onReset?: Function;
}

enum FormEnum {
  cardID,
  cardNum,
  embossName,
  availBalance,
}

type ValidDataSchemeFormData = {
  [key in keyof typeof FormEnum]: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

type ProfileScreenRouteProp = RouteProp<StackParamsList, ScreenNameType.CARD_STATEMENT>;

const FilterCardStatementTable = (props: FilterCardStatementTableProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const isDesktop = useDesktopMediaQuery();
  // ĐĂNG KÍ NGÔN NGỮ TIẾNG VIỆT
  registerTranslation("vi", vi);

  const { params } = useRoute<ProfileScreenRouteProp>();

  const listCard = useSelector(getCardListSelector);

  const { onChangeSearch = () => {}, showModal, closeModal, onReset = () => {} } = props;
  const [cardSelect, setCardSelect] = React.useState<CARDITEM | undefined>(params?.card);

  // Instance Form Method
  const { control, handleSubmit, getValues, setValue, clearErrors, trigger } = useForm<FormFilterCardStatement>({
    defaultValues: {
      cardID: "",
      cardNum: "",
    },
    mode: "onChange",
  });

  const validateSchemeForm: ValidDataSchemeFormData = {
    cardID: {
      required: {
        value: false,
        message: "",
      },
    },
    cardNum: {
      required: {
        value: true,
        message: t("CARD_THONG_BAO_CHUA_CHON_THE"),
      },
      validate: {
        status: (value) => {
          if (value?.length) {
            if (cardSelect && !ArrayCreditShow.includes(cardSelect?.hotStatus || "")) {
              return getCardValidStatus(cardSelect?.hotStatus);
            }
          }
          return true;
        },
      },
    },
    embossName: {
      required: {
        value: false,
        message: "",
      },
    },
    availBalance: {
      required: {
        value: false,
        message: "",
      },
    },
  };

  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        textInput: {},
        buttonContainer: { alignSelf: "center" },
      }),
    [],
  );

  const requestSearch = React.useCallback(
    (value: FormFilterCardStatement) => {
      onChangeSearch(value);
    },
    [onChangeSearch],
  );

  const onPressShowHistory = React.useCallback(() => {
    handleSubmit(requestSearch).call(() => {});
  }, [handleSubmit, requestSearch]);

  const handlerSelectCard = React.useCallback(
    (_card: CARDITEM) => {
      closeModal();
      console.log("handlerSelectCard: ", _card);
      if (_card.cardId?.length) {
        clearErrors("cardNum");
        setCardSelect(_card);
      }
    },
    [clearErrors, closeModal],
  );

  const onPressCard = () => {
    if (!listCard?.length) dispatch(fetchCardListRequest());
    showModal(
      <CardSourceModal
        onPressAcceptCard={handlerSelectCard}
        typeArray={[Constant.CARD.CARD_TYPE.CREDIT]}
        errorMessage={t("CARD_BAN_CHUA_DANG_KI_LOAI_THE_TIN_DUNG_NAY")}
        statusArrayCredit={ArrayCreditShow}
      />,
    );
  };

  React.useEffect(() => {
    if (cardSelect) {
      if (cardSelect.cardId !== getValues("cardID")) onReset();
      setValue("cardID", cardSelect.cardId || "");
      setValue("cardNum", cardSelect.cardNum || "");
      setValue("embossName", cardSelect.embossName || "");
      setValue("availBalance", formatNumberThousand(cardSelect?.avalBalance));
      trigger("cardNum");
    }
  }, [cardSelect, getValues, setValue, onReset, trigger]);

  React.useEffect(() => {
    if (!params?.card && !cardSelect) {
      if (listCard && listCard.length > 0) {
        setCardSelect(listCard[0]);
      }
    }
  }, [cardSelect, listCard, params?.card]);

  return (
    <>
      <Block flex={1} bg={colors.backgroundForm} br={12} p={spacing.large}>
        <Block row={isDesktop} center={isDesktop}>
          <Block flex={1}>
            <InputSelect
              control={control}
              name="cardNum"
              label={t("CARD_TAI_KHOAN_THE")}
              rules={validateSchemeForm.cardNum}
              style={styles.textInput}
              onClick={onPressCard}
            />
          </Block>
        </Block>
        <Block spaced={isDesktop} row={isDesktop}>
          <Block flex={1}>
            <InputText control={control} inputProps={{ editable: false }} name="embossName" label={t("CARD_TEN_CHU_THE")} />
          </Block>
          <QueueSpacing size="huge" />

          <Block flex={1}>
            <InputText control={control} inputProps={{ editable: false }} name="availBalance" label={t("CARD_SO_DU_KHA_DUNG")} />
          </Block>
        </Block>
      </Block>
      <Block mt={spacing.large}>
        <Button onPress={onPressShowHistory} uppercase={false} mode="contained" style={styles.buttonContainer}>
          {t("ACC_XEM_SAO_KE")}
        </Button>
      </Block>
    </>
  );
};

export default FilterCardStatementTable;
